import React from "react";

import { useRouter } from "next/router";
import VideoChat from "public/icons/cam.svg";
import LiveChat from "public/icons/live-chat.svg";
import PlayCircleIcon from "public/icons/play_circle_small_icon.svg";

import { Theme, useMediaQuery } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import useChatWindow from "@/hooks/useChatOpen";
import { isFeatureFlagEnabled } from "@/lib/feature-flags";
import { pickFirstTrueMatchingValue } from "@/utils/pickFirstTrueMatchingValue.utils";

type CardBottomButtonPropTypes = {
  isOnline?: boolean;
  hasIntroVideo?: boolean;
  isVideochatAvailable?: boolean;
  ladyId?: number;
  openRegModalHandler: (
    header: string,
  ) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

export const CardBottomButtonRenderer: React.FC<CardBottomButtonPropTypes> = ({
  isOnline,
  hasIntroVideo,
  isVideochatAvailable,
  ladyId,
  openRegModalHandler,
}) => {
  const downXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("xs"));
  const downXxs = useMediaQuery((theme: Theme) => theme.breakpoints.down(410));

  const { isAuthenticated } = useAuthContext();
  const openChatWindow = useChatWindow(ladyId);
  const { push } = useRouter();

  const possibleOnClickHandlers = {
    redirectToLadyPage: () => push(`/lady/id${ladyId}`),
    openChatWindow: () => openChatWindow("liveChat"),
    openRegModal: openRegModalHandler("Chatting with Me"),
  };

  const onClickHandler = pickFirstTrueMatchingValue([
    [isAuthenticated, possibleOnClickHandlers.openChatWindow],
    [hasIntroVideo, possibleOnClickHandlers.redirectToLadyPage],
    [true, possibleOnClickHandlers.openRegModal],
  ]);

  const rendererConfig = [
    [
      hasIntroVideo &&
        !isAuthenticated &&
        !isFeatureFlagEnabled("LADY_CARD_VIEW_VIDEO_BUTTON_HIDDEN"),
      {
        StartIcon: downXs ? null : <PlayCircleIcon />,
        startIconClassName: "mb-[2px]",
        buttonText: "view video",
        ariaLabel: "View video",
      },
    ],
    [
      isVideochatAvailable,
      {
        StartIcon: downXs ? null : <VideoChat />,
        startIconClassName: "mb-[4px]",
        buttonText: "live chat",
        ariaLabel: "Chat with me",
      },
    ],
    [
      isOnline,
      {
        StartIcon: downXs ? null : <LiveChat />,
        startIconClassName: "mt-1",
        buttonText: "live chat",
        ariaLabel: "Chat with me",
      },
    ],
    [
      true,
      {
        StartIcon: null,
        startIconClassName: "",
        buttonText: "",
        ariaLabel: "",
      },
    ],
  ] satisfies Parameters<typeof pickFirstTrueMatchingValue>[0];

  const { StartIcon, buttonText, startIconClassName, ariaLabel } =
    pickFirstTrueMatchingValue(rendererConfig);

  /**
   * Dont delete this. This is for checking the onClickHandler visually
   * and not clicking on the button
   */
  // const _debugValue_functionLabel = Object.entries(
  //   possibleOnClickHandlers,
  // ).find(([key, func]) => {
  //   return func === onClickHandler ? key : false;
  // })?.[0];

  return !!buttonText ? (
    <div
      className={`absolute ${
        downXs ? "bottom-2" : "bottom-4"
      } left-0 right-0 flex justify-center`}
    >
      <Button
        startIcon={StartIcon}
        startIconClassName={startIconClassName}
        variant="primary"
        onHoverVariant="fade-out"
        className={`w-fit text-sm ${
          downXs ? (downXxs ? "h-6" : "h-7") : "h-8"
        }`}
        onClick={onClickHandler}
        aria-label={ariaLabel}
      >
        {buttonText}
      </Button>
      {/* Don't delete the comment */}
      {/* <span className="text-sm bg-grey1-800 absolute -top-8 left-7">
        {_debugValue_functionLabel}
      </span> */}
    </div>
  ) : null;
};
